import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {API_BASE_URL} from '../../configs/AppConfig';
import service from "../../auth/FetchInterceptor";
import {fetchLinks, fetchLinksByFilter} from "./linksSlice";
import {SORT_ORDER} from "../../constants/Constants";


// Async thunk to fetch categories
export const fetchCategories = createAsyncThunk(
    'categories/fetchCategories',
    async ({page = 1, pageSize = 20, sorter={}, filters, t}, {getState, rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams({
                page: page - 1,
                size: pageSize,
                ...getState().categories.filters
            });
            // Handle sorting if sorter is provided
            if (sorter.field && sorter.order) {
                // Columns sorter sets order as 'ascend' but we send as 'asc'
                const sortOrder = sorter.order === SORT_ORDER.ASCEND ? SORT_ORDER.ASC : SORT_ORDER.DESC;
                queryParams.append(SORT_ORDER.SORT, `${sorter.field},${sortOrder}`);
            }
             // API call
            return await service.get(`${API_BASE_URL}/api/categories?${queryParams.toString()}`); // Return the fetched data
        } catch (error) {
            return rejectWithValue(error.response?.data || t('error.failed.to.load.categories'));
        }
    }
);

// Async thunk to fetch categories
export const fetchCategoriesByFilter = createAsyncThunk(
    'categories/fetchCategoriesFiltered',
    async ({page = 1, pageSize = 20, sorter={}, filters, t}, { rejectWithValue }) => {
        try {
            // Convert filters object into query params
            const queryParams = new URLSearchParams({
                page: page - 1, // Backend uses 0-based index
                size: pageSize,
                ...filters
            });
            // Handle sorting if sorter is provided
            if (sorter.field && sorter.order) {
                // Columns sorter sets order as 'ascend' but we send as 'asc'
                const sortOrder = sorter.order === SORT_ORDER.ASCEND ? SORT_ORDER.ASC : SORT_ORDER.DESC;
                queryParams.append(SORT_ORDER.SORT, `${sorter.field},${sortOrder}`);
            }
            // API call
            return await service.get(`${API_BASE_URL}/api/categories-filtered?${queryParams.toString()}`); // Return the fetched data
        } catch (error) {
            return rejectWithValue(error.response?.data || t('error.failed.to.search.categories'));
        }
    }
);

// Add a new category
export const addCategory = createAsyncThunk(
    "categories/addCategory",
    async ({newCategory, t}, {dispatch, getState, rejectWithValue }) => {
        try {
            const response = await service.post(`${API_BASE_URL}/api/categories`, newCategory);
            dispatch(fetchCategories({ page: 1, pageSize: getState().categories.pagination.pageSize, t:t }));
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || t("error.adding.category"));
        }
    }
);

// Update a category
export const updateCategory = createAsyncThunk(
    "categories/updateCategory",
    async ({ id, updatedData, t}, {dispatch, getState, rejectWithValue }) => {
        try {
            const response = await service.put(`${API_BASE_URL}/api/categories/${id}`, updatedData);
            dispatch(fetchCategories({ page: 1, pageSize: getState().categories.pagination.pageSize, t:t }));
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || t("error.updating.category"));
        }
    }
);

// Delete a category
export const deleteCategory = createAsyncThunk(
    "categories/deleteCategory",
    async ({id, t}, {dispatch, getState, rejectWithValue }) => {
        try {
            await service.delete(`${API_BASE_URL}/api/categories/${id}`);

            // Get current pagination state
            const { pagination, data } = getState().categories;

            // Calculate the new total count
            const newTotal = pagination.total - 1;

            // If deleting the last item on the last page, move to previous page
            const newPage =
                pagination.current > 1 && data.length === 1
                    ? pagination.current - 1
                    : pagination.current;

            dispatch(fetchCategories({ page: newPage, pageSize: pagination.pageSize, t:t}));
            return id; // Return ID so we can remove it from state
        } catch (error) {
            return rejectWithValue(error.response?.data || t("error.deleting.category"));
        }
    }
);

// Create the slice
const categoriesSlice = createSlice({
    name: 'categories',
    initialState: {
        data: [],
        loading: false,
        error: null,
        pagination: {
            current: 1,
            pageSize: 8,
            total: 0,
        },
        sorter: { field: null, order: null },
        filters: {},
    },
    reducers: {
        setPagination: (state, action) => {
            state.pagination.current = action.payload.current;
            state.pagination.pageSize = action.payload.pageSize;
        },
        setSorter: (state, action) => {
            state.sorter = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        }
    }, // No extra reducers needed for now
    extraReducers: (builder) => {
        builder
            .addCase(fetchCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload.content;
                state.pagination = {
                    current: action.payload.number + 1, // Adjust for 0-based index
                    pageSize: action.payload.size,
                    total: action.payload.totalElements,
                };
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchCategoriesByFilter.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload.content;
                state.pagination = {
                    current: action.payload.number + 1, // Adjust for 0-based index
                    pageSize: action.payload.size,
                    total: action.payload.totalElements,
                };
            })
            .addCase(addCategory.fulfilled, (state, action) => {
                state.data.push(action.payload);
            })
            .addCase(updateCategory.fulfilled, (state, action) => {
                const index = state.data.findIndex(cat => cat.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
            })
            .addCase(deleteCategory.fulfilled, (state, action) => {
                state.data = state.data.filter(cat => cat.id !== action.payload);
            });
    },
});
export const { setPagination, setSorter, setFilters } = categoriesSlice.actions;
export default categoriesSlice.reducer;